// Npm modules
import JsSIP from 'jssip';
import type { UserAgent as IUserAgent, UserAgentAudio } from './interfaces';
// @ts-expect-error
import RequestSender from 'jssip/lib/RequestSender';

import SIPMessage from 'jssip/lib/SIPMessage';
// @ts-expect-error
import Transactions from 'jssip/lib/Transactions';
// App modules
import { logActivity } from '../helpers/activityLog';
import Call from './Call';
import { stringToUriObject } from './util';

// if (process.env.NODE_ENV === 'production') {
//   JsSIP.debug.disable('*');
// } else {
//   JsSIP.debug.enable('*');
// }

JsSIP.debug.disable('*');

const parser = new DOMParser();
class UserAgent extends JsSIP.UA implements IUserAgent {
  public audio: UserAgentAudio;
  private uuid: string;
  public isActive = true;
  public ongoingCalls: any[];
  private subscriptions: Map<string, any>;
  private subscribeExpires: number;
  public domain: string;

  private _receiveRequest: (request: any) => void;
  private receiveRequest: (request: any) => void;

  constructor(config: {
    audio: UserAgentAudio;
    domain: string;
    username?: string;
    uri?: string;
    userAgent?: string;
  }) {
    const { domain, ...restConfig } = config;

    const properConfig: any = {
      ws_servers: 'wss://sipws.sipcentric.com',
      register_expires: 60,
      session_timers: false,
      connection_recovery_max_interval: 30,
      connection_recovery_min_interval: 2,
      ...restConfig,
    };

    if (config.username) {
      // const uriParts = Utils.normalizeTarget(
      //   `sip:${config.username}@${domain}`,
      //   domain,
      // );
      // config.uri = new URI(
      //   uriParts.scheme,
      //   uriParts.user,
      //   uriParts.host,
      //   uriParts.port,
      // );
      properConfig.uri = `sip:${config.username}@${domain}`;
    }

    if (config.userAgent) {
      // @ts-expect-error
      JsSIP.C.USER_AGENT = config.userAgent;
    }

    properConfig.sockets = [
      new JsSIP.WebSocketInterface(properConfig.ws_servers),
    ];

    const { audio } = config;

    super(properConfig);

    this.uuid = crypto.randomUUID();

    logActivity(this.uuid, 'CONSTRUCTOR', { uri: properConfig.uri });

    (this as any).uriString = properConfig.uri;

    this.domain = domain;

    this.audio = audio;

    this.ongoingCalls = [];

    (this as any).sessions = {};

    this.subscriptions = new Map();

    this.subscribeExpires = 600;

    this.isActive = true;

    // Resubscribe checker
    setInterval(() => {
      this.subscriptions.forEach((subscription) => {
        const { expires, subscriptionState } = subscription;
        if (subscriptionState === 'expired') {
          this.subscribeCustom(
            subscription.toUser,
            subscription.toDomain,
            subscription,
          );
          return;
        }
        const resubscribeTime = 50000;
        const now = Date.now();
        const diff = expires - now;
        if (diff < resubscribeTime) {
          this.resubscribe(subscription);
        }
      });
    }, 30000);

    this.on('newRTCSession', (e: any) => {
      if (e.originator === 'remote') {
        const call = new Call(this, 'IN', e.session);

        const sipcentricTags = e.request.headers['X-Sipcentric-Tags'];
        if (sipcentricTags) {
          const rawTags = sipcentricTags[0].raw;
          const tags = JSON.parse(rawTags);
          call.tags = tags;
        }

        this.addOngoingCall(call);
        this.emit('incomingCall', call);
      }
    });

    (this as any).cseq = 0;

    // Rename receiveRequest to _receiveRequest
    // @ts-expect-error
    this._receiveRequest = this.receiveRequest;

    // Define a new receiveRequest handler that checks for NOTIFYs
    // If the request is a NOTIFY it handles it,
    // otherwise it passes the request off to _receiveRequest
    this.receiveRequest = (request) => {
      // If we've got a NOTIFY
      if (
        request.method === JsSIP.C.NOTIFY &&
        request.headers.Event[0].raw === 'dialog'
      ) {
        // JsSIP transaction check
        if (Transactions.checkTransaction(this, request)) {
          return;
        }

        // This is horrible but required
        new Transactions.NonInviteServerTransaction(
          this,
          request.transport,
          request,
        );

        try {
          const subscriptionStateHeader =
            request.headers['Subscription-State'][0].raw;
          if (subscriptionStateHeader) {
            const subscriptionState = subscriptionStateHeader.split(';')[0];

            this.updateSubscriptionState(request);

            if (subscriptionState === 'terminated') {
              return;
            }
          }
        } catch (err) {
          console.debug('err: ', err);
        }

        if (!request.body) {
          return;
        }

        // Parse the xml request body
        const parsedBody = parser.parseFromString(
          request.body,
          'application/xml',
        );

        try {
          // Grab the state and version from the parsed body
          const state =
            parsedBody.children[0]?.children[0]?.textContent?.trim();
          const version = parseInt(
            parsedBody.children[0]?.getAttribute('version') || '0',
            10,
          );

          // Find the subscription
          const subscription = this.getSubscription(request);

          if (subscription) {
            if (version > subscription.version) {
              this.updateVersion(request, version);
              this.emitUserStateChanged(subscription.toUser, state);
            }
            // Reply OK
            request.reply(200);
          }
        } catch (_) {
          console.debug('Error parsing xml: ', request);
        }
      } else {
        // Pass the request off to _receiveRequest
        return this._receiveRequest(request);
      }
    };
  }

  emitUserStateChanged(user: any, state: string) {
    let mappedState;

    switch (state) {
      case 'terminated': {
        mappedState = 'ACTIVE';
        break;
      }
      case 'confirmed':
      case 'Trying': {
        mappedState = 'INACTIVE';
        break;
      }
      case 'early': {
        mappedState = 'RINGING';
        break;
      }
      default: {
        mappedState = 'UNKNOWN';
        break;
      }
    }

    if (mappedState) {
      this.emit('userStateChanged', user, mappedState);
    }
  }

  getSubscription(message: any) {
    return this.subscriptions.get(message.call_id);
  }

  updateVersion(request: any, version: any) {
    const subscription = this.getSubscription(request);
    if (subscription) {
      this.subscriptions.set(request.call_id, {
        ...subscription,
        version,
      });
    }
  }

  updateSubscriptionState(request: any) {
    if (!request.headers['Subscription-State']) return;

    const subscriptionStateHeader: string =
      request.headers['Subscription-State'][0].raw;
    const splitHeaders = subscriptionStateHeader.split(';');

    const state = splitHeaders[0];
    const secToExpire = splitHeaders
      .find((x) => x.includes('expires'))
      ?.split('=')[1];
    const msToExpire = Number(secToExpire) * 1000;

    const subscription = this.getSubscription(request);
    if (subscription) {
      clearTimeout(subscription.expireTimeout);
      this.subscriptions.set(request.call_id, {
        ...subscription,
        subscriptionState: state,
        expires:
          secToExpire === undefined
            ? subscription.expires
            : Date.now() + msToExpire,
        expireTimeout: setTimeout(() => {
          this.expireSubscription(request);
        }, msToExpire),
      });
    }
  }

  storeSubscription(response: any) {
    const callId = response.call_id;
    const msToExpire = response.headers.Expires[0].raw * 1000;
    const expires = Date.now() + msToExpire;
    const subscription = this.getSubscription(response);
    const version = subscription ? subscription.version : -1;
    const rawContact = response.headers.Contact[0].raw;
    const contact = rawContact.replace(/[<,>]/g, '');
    if (subscription?.expireTimeout) {
      clearTimeout(subscription.expireTimeout);
    }

    this.subscriptions.set(callId, {
      callId,
      version,
      expires,
      contact,
      toUser: response.to.uri.user,
      toDomain: response.to.uri.host,
      cseq: response.cseq,
      toTag: response.to_tag,
      fromTag: response.from_tag,
      expireTimeout: setTimeout(() => {
        this.expireSubscription(response);
      }, msToExpire),
    });
  }

  expireSubscription(response: any) {
    const callId = response.call_id;
    const subscription = this.getSubscription(response);
    if (subscription) {
      this.subscriptions.set(callId, {
        ...subscription,
        subscriptionState: 'expired',
      });
    }
  }

  clearSubscriptions() {
    this.subscriptions.forEach((subscription) => {
      const { expireTimeout } = subscription;
      clearTimeout(expireTimeout);
    });
    this.subscriptions.clear();
  }

  connect() {
    return this.start();
  }

  // TODO jssip seems to have built-in subscribe capabilities now
  subscribeCustom(user: string, domain: string, replacesSubscription?: any) {
    if (!this.isConnected()) {
      return;
    }

    const { Utils } = JsSIP;

    let extraHeaders: string[] = [];
    extraHeaders = extraHeaders.slice();
    extraHeaders.push(
      `Contact: ${this.contact.toString()}; expires=${this.subscribeExpires}`,
    );
    extraHeaders.push(`Expires: ${this.subscribeExpires}`);
    extraHeaders.push('Event: dialog');

    const uri = stringToUriObject(`sip:${user}@${domain}`, this);

    let callId;

    if (replacesSubscription) {
      callId = replacesSubscription.callId;
    } else {
      // @ts-expect-error
      callId = Utils.createRandomToken(22);
    }

    const request = new SIPMessage.OutgoingRequest(
      // @ts-expect-error The type definition in jssip is wrong
      JsSIP.C.SUBSCRIBE,
      uri,
      this,
      {
        to_uri: uri,
        call_id: callId,
        cseq: 1,
      },
      extraHeaders,
    );

    const requestSender = new RequestSender(this, request, {
      onReceiveResponse: this.subscribeResponseHandler.bind(this),
      onRequestTimeout: () => {
        console.debug('timeout');
      },
      onTransportError: () => {
        console.debug('transport error');
      },
    });

    requestSender.send();
  }

  resubscribe(subscription: any) {
    if (!this.isConnected()) {
      return;
    }

    let extraHeaders: string[] = [];
    extraHeaders = extraHeaders.slice();
    extraHeaders.push(
      `Contact: ${this.contact.toString()}; expires=${this.subscribeExpires}`,
    );
    extraHeaders.push(`Expires: ${this.subscribeExpires}`);
    extraHeaders.push('Event: dialog');

    const uri = stringToUriObject(
      `sip:${subscription.toUser}@${subscription.toDomain}`,
      this,
    );
    // const contactUri = stringToUriObject(subscription.contact, this);

    const request = new SIPMessage.OutgoingRequest(
      // @ts-expect-error The type definition in jssip is wrong
      JsSIP.C.SUBSCRIBE,
      uri,
      this,
      {
        to_uri: uri,
        call_id: subscription.callId,
        cseq: subscription.cseq + 1,
        from_tag: subscription.fromTag,
        to_tag: subscription.toTag,
      },
      extraHeaders,
    );

    const requestSender = new RequestSender(this, request, {
      onReceiveResponse: this.subscribeResponseHandler.bind(this),
      onRequestTimeout: () => {
        console.debug('timeout');
      },
      onTransportError: () => {
        console.debug('transport error');
      },
    });

    requestSender.send();
  }

  subscribeResponseHandler(response: any) {
    const statusCode = response.status_code;

    if (statusCode.toString().match(/20\d/)) {
      this.storeSubscription(response);
    }
  }

  dial(target: string, options: any) {
    // Before setting up the call, place
    // all other calls on hold
    this.ongoingCalls.forEach((call) => {
      if (!call.isOnHold) {
        call.hold();
      }
    });
    return new Call(this, 'OUT', target, options);
  }

  addOngoingCall(call: any) {
    const callExists = !!this.getOngoingCall(call.session.id);
    logActivity(this.uuid, 'ONGOING_ADD', {
      direction: call.direction,
      sessionId: call.session.id,
      ongoingCallIds: this.ongoingCalls.map((call) => call.session.id),
      callExists,
    });
    if (!callExists) {
      this.ongoingCalls.push(call);
    }
  }

  removeOngoingCall(sessionId: string) {
    logActivity(this.uuid, 'ONGOING_REMOVE', {
      sessionId,
      ongoingCallIds: this.ongoingCalls.map((call) => call.session.id),
    });
    this.ongoingCalls = this.ongoingCalls.filter(
      (c) => c.session.id !== sessionId,
    );
  }

  getOngoingCall(sessionId: string) {
    return this.ongoingCalls.find((c) => c.session.id === sessionId);
  }

  getActiveCall() {
    return this.ongoingCalls.find((c) => !c.isOnHold);
  }

  setVolume(volume: number) {
    if (!this.audio.remote) {
      return;
    }
    this.audio.remote.audioElement.volume = volume;
  }
}

export default UserAgent;
